import axios from "axios";
import {
  CardStatusResponse,
  NewCardStatusResponse,
  KYCStatus,
  RainStatus,
  SumSubTokenResponse,
  CreditDelegationRequest,
  CreditDelegationResponse,
  LinkCardRequest,
  UserCardsResponse,
  CreditInformationResponse,
  CreateCardResponse,
  TransactionsResponse,
  OnchainTransactionDetailsResponse,
  UpdateCardRequest,
  CardLimitFrequency,
  EulerTotalAssetResponse,
  UserParams,
  UserConfig,
  HookType,
  HookConfig,
  HookTransaction,
} from "./types";
import { getApiClient } from "./httpClient";
import {
  formatAvailableLimit,
  generateUUID,
  getCardSolverConfig,
  mapEulerMetadataToSuppliedAssets,
  mapNewStatusToLegacyCardStatus,
} from "./utils";
import {
  getProviderConfig,
  CreditProviderId,
  isCreditProviderOfType,
  HYPER_EVM_CHAIN_ID,
  BASE_CHAIN_ID,
} from "../config/creditRegistry";
import {
  getMockCardStatus,
  getMockUserCards,
  getMockAaveCreditInformation,
  getMockEulerCreditInformation,
  getMockSumSubToken,
  getMockCreditDelegationTxData,
  getMockTransactions,
  transactionsMockData,
  getMockNewCardStatus,
} from "./mockData";
import { useSDKConfigStore } from "../stores/sdkConfigStore";

/**
 * Extracts a user-friendly error message from an Axios error object.
 *
 * @param error - The error object to extract the message from.
 * @param fallback - The message to return if no specific error is found. Defaults to `"Unexpected error"`.
 * @returns The extracted error message or the fallback string.
 */
const extractErrorMessage = (error: any, fallback = "Unexpected error") =>
  error?.response?.data?.data?.error ||
  error?.response?.data?.message ||
  error?.message ||
  fallback;

/**
 * Fetches the current card status for a given EOA address.
 *
 * @returns The card status as a `CardStatusResponse`.
 * @throws Will throw an error if the request fails (except 404).
 */
export const getCardStatus = async (): Promise<CardStatusResponse> => {
  // const mockResponse = getMockNewCardStatus("rain_rejection_error");
  // const legacyStatus = mapNewStatusToLegacyCardStatus(
  //   mockResponse.data.kycStatus,
  //   mockResponse.data.rainStatus,
  //   mockResponse.data.errorDetails
  // );

  // return { data: legacyStatus };
  // Real API call (commented out - uncomment to use real API)
  try {
    const client = getApiClient();
    const response = await client.get<NewCardStatusResponse>(
      `/cards/users/status`
    );

    // Map new response format to legacy format for backward compatibility
    const { kycStatus, rainStatus, errorDetails } = response.data.data;
    const legacyStatus = mapNewStatusToLegacyCardStatus(
      kycStatus,
      rainStatus,
      errorDetails
    );

    return { data: legacyStatus };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return { data: "userNotExisting" };
    }
    throw error;
  }
};

/**
 * Retrieves all cards linked to a specific user.
 *
 * @returns A `UserCardsResponse` object with details of linked cards, or `{ data: null }` on failure.
 */
export const getUserCards = async (): Promise<UserCardsResponse> => {
  // Return mock data
  // return getMockUserCards();

  // Real API call (commented out - uncomment to use real API)
  try {
    const client = getApiClient();
    const response = await client.get<UserCardsResponse>(`/cards`);
    return response.data;
  } catch (err) {
    console.error("[getUserCards]", err);
    return { data: null };
  }
};

/**
 * Fetches credit information for a given credit address and solver type.
 *
 * @param solverType - The type of solver ("aave-v3", "euler", "hypurrFi", or custom).
 * @returns A `CreditInformationResponse` containing available limit, delegation amount, and metadata.
 */
export const getCreditInformation = async (
  solverType: CreditProviderId,
  chainID: number,
  userConfig?: UserConfig["config"]
): Promise<CreditInformationResponse> => {
  // Return mock data based on credit type
  // if (solverType === "aave-v3" || solverType === "hypurrFi") {
  //   return getMockAaveCreditInformation();
  // } else {
  //   return getMockEulerCreditInformation();
  // }

  // Real API call (commented out - uncomment to use real API)
  try {
    const client = getApiClient();

    // Registry is single source of truth - no hardcoded conditions
    const config =
      userConfig ?? getProviderConfig(solverType as CreditProviderId) ?? {};

    const response = await client.post<CreditInformationResponse>(
      `/cards/users/credit`,
      {
        solverType,
        config,
        chainID,
      }
    );

    // Transform availableLimit if it exists
    if (
      response.data?.data &&
      typeof response.data.data.availableLimit === "number"
    ) {
      response.data.data.availableLimit = formatAvailableLimit(
        response.data.data.availableLimit,
        solverType,
        userConfig?.purchaseBaseTokenAddress || undefined
      );
      response.data.data.amountDelegated = formatAvailableLimit(
        response.data.data.amountDelegated,
        solverType,
        userConfig?.purchaseBaseTokenAddress || undefined
      );
    }

    // If Euler, map vaults to suppliedAssets for compatibility (without mutating original)
    if (solverType === "euler" && response.data?.data?.metadata) {
      response.data.data.metadata = mapEulerMetadataToSuppliedAssets(
        response.data.data.metadata
      );
    }

    return response.data;
  } catch (error) {
    console.error("[getCreditInformation]", error);
    return { data: null };
  }
};

/**
 * Retrieves a SumSub KYC access token for the given user.
 *
 * @param userId - The user ID in the system.
 * @param ipAddress - Optional IP address of the user.
 * @returns The SumSub token data, or `undefined` if the request fails.
 */
export const getSumSubAccessToken = async (
  userId: string,
  ipAddress?: string
): Promise<SumSubTokenResponse["data"] | undefined> => {
  // Return mock data
  // return getMockSumSubToken();

  // Real API call (commented out - uncomment to use real API)
  try {
    const client = getApiClient();
    const response = await client.post<SumSubTokenResponse>(
      "/cards/sumsub/token",
      { ownerAddress: userId, ipAddress }
    );
    return response.data.data;
  } catch (error) {
    console.error("[error at getSumSubAccessToken]", error);
    return undefined;
  }
};

/**
 * Generates transaction data for delegating credit.
 *
 * @param requestData - The delegation request payload.
 * @param solverParams - Optional solver configuration parameters.
 * @returns An object containing the credit delegation transaction data.
 */
export const getCreditDelegationTxData = async (
  requestData: CreditDelegationRequest,
  solverParams?: {
    aTokenAddress: string;
    purchaseBaseTokenAddress: string;
    purchaseAssetAddress: string;
    purchaseAllocationPercent: number;
    purchasePoolAddress: string;
  }
): Promise<{ data: CreditDelegationResponse }> => {
  // Return mock data
  // return getMockCreditDelegationTxData();

  // Real API call (commented out - uncomment to use real API
  const payload = {
    creditType: requestData.creditType,
    creditLimit: requestData.creditLimit,
    chainID: requestData.chainID,
    userParams: {
      ...requestData.userParams,
      ...(solverParams ?? {}),
    },
  };
  const client = getApiClient();
  const response = await client.post<{ data: CreditDelegationResponse }>(
    `/cards/users/credit/delegate`,
    payload
  );
  return response.data;
};

/**
 * Links an existing card to a specific credit account.
 *
 * @param requestData - The link request payload.
 * @returns The API response object.
 */
export const linkCardToCreditAccount = async (
  requestData: LinkCardRequest
): Promise<any> => {
  const client = getApiClient();
  const response = await client.patch("/cards/", requestData);
  return response.data;
};

/**
 * Updates a user's card by linking it to a credit provider using signed authorization.
 *
 * @param requestData - The update request containing credit provider details.
 * @returns An object indicating success or failure, with an optional error message.
 */
export const updateCard = async (
  requestData: UpdateCardRequest
): Promise<{ success: boolean; message?: string }> => {
  try {
    const client = getApiClient();

    // Use provided config if available, otherwise fall back to default config
    const cardSolverConfig = isCreditProviderOfType(
      requestData.creditType,
      "spend"
    )
      ? { ...requestData.config }
      : getCardSolverConfig(requestData.creditType);

    const response = await client.patch(`/cards/creditprovider`, {
      creditProviderAddress: requestData.creditProviderAddress,
      chainID: requestData.chainId,
      solverType: requestData.creditType,
      cardID: requestData.cardID,
      cardSolverConfig,
    });

    if (response.status === 200) {
      return { success: true };
    }

    return {
      success: false,
      message: response.data?.message || "Failed to update card",
    };
  } catch (error: any) {
    console.error("[updateCard]", error);
    return {
      success: false,
      message:
        error?.response?.data?.data?.error ||
        error?.message ||
        "Failed to update card",
    };
  }
};

/**
 * Creates a new virtual or physical card for a user.
 *
 * @param userEoa - The Ethereum address (EOA) of the user.
 * @param chainID - The blockchain network's chain ID.
 * @param solverType - The type of credit provider (solver).
 * @returns The API response containing card creation details.
 */
export const createCard = async (
  userEoa: string,
  chainID: number,
  solverType: CreditProviderId,
  cardSolverConfig: Record<string, string>
): Promise<CreateCardResponse> => {
  const client = getApiClient();

  const response = await client.post<CreateCardResponse>(`/cards/create`, {
    creditProviderAddress: userEoa,
    chainID: chainID,
    solverType: solverType,
    cardSolverConfig,
  });

  return response.data;
};

/**
 * Initializes the configuration for a specific solver type for a user.
 *
 * @param chainID - The blockchain network's chain ID.
 * @param solverType - The type of solver to initialize.
 * @param config - A record of configuration key-value pairs.
 * @returns An object containing success status and message.
 */
export const initializeUserConfig = async (
  chainID: number,
  solverType: CreditProviderId,
  config: Record<string, string>
): Promise<{
  success: boolean;
  message?: string;
}> => {
  const client = getApiClient();

  try {
    const response = await client.post<CreateCardResponse>(`/cards/users`, {
      solverType,
      config,
      chainID,
    });

    if (response.status === 200) {
      return { success: true };
    }

    return {
      success: false,
      message: "Configuration initialized successfully.",
    };
  } catch (err: any) {
    return {
      success: false,
      message:
        err?.response?.data?.data?.error ||
        err?.message ||
        "Failed to initialize configuration.",
    };
  }
};

/**
 * Retrieves the configuration for a specific credit type.
 *
 * @returns A promise that resolves with the configuration object or null if an error occurs.
 */
export const getUserConfig = async (): Promise<UserConfig | null> => {
  try {
    const client = getApiClient();
    const response = await client.get<{ data: UserConfig }>(
      `/cards/users/preferences`
    );
    return response.data.data;
  } catch (error) {
    console.error("Failed to fetch user config:", error);
    return null;
  }
};

/**
 * Freezes or unfreezes a user's card using a signed request.
 *
 * @param cardID - The UUID of the card.
 * @param freeze - Whether to freeze (`true`) or unfreeze (`false`) the card.
 * @returns An object indicating success or failure with an optional message.
 */
export const freezeCard = async (
  cardID: string,
  freeze: boolean
): Promise<{ success: boolean; message?: string }> => {
  const client = getApiClient();

  try {
    const response = await client.patch(`/cards/status`, {
      cardID,
      freeze,
    });
    // if 200 then return success
    if (response.status === 200) {
      return { success: true };
    }
    // if not 200, return failure with message
    return {
      success: false,
      message: response.data?.message || "Failed to freeze card",
    };
  } catch (error: any) {
    console.error("[freezeCard]", error);
    return {
      success: false,
      message:
        error?.response?.data?.data?.error ||
        error?.message ||
        "Failed to freeze card",
    };
  }
};

/**
 * Updates the PIN of a card by sending encrypted PIN data.
 *
 * @param iv - Base64-encoded initialization vector (IV) used for encryption.
 * @param data - Base64-encoded encrypted PIN.
 * @returns An object indicating success or failure with an optional message.
 */
export const updatePin = async (
  iv: string,
  data: string,
  sessionID: string,
  cardID: string
): Promise<{ success: boolean; message?: string }> => {
  const client = getApiClient();

  try {
    const response = await client.patch<{ success: boolean; message?: string }>(
      `/cards/pin`,
      { pin: { encryptedPin: { data, iv } }, cardID },
      {
        headers: {
          sessionID,
        },
      }
    );

    if (response.status === 200) {
      return { success: true };
    }

    return {
      success: false,
      message: response.data?.message || "Failed to update PIN",
    };
  } catch (error: any) {
    console.error("[updatePin]", error);
    return {
      success: false,
      message:
        error?.response?.data?.data?.error ||
        error?.message ||
        "Failed to update PIN",
    };
  }
};

/**
 * Retrieves encrypted card details including PAN and CVC.
 *
 * @param cardID - The UUID of the card.
 * @returns An object containing encrypted PAN, CVC, and card expiration date.
 * @throws If the request fails or returns invalid data.
 */
export const getCardSecrets = async (
  cardID: string,
  sessionID: string
): Promise<{
  encryptedPan: { iv: string; data: string };
  encryptedCvc: { iv: string; data: string };
  ExpirationMonth: string;
  ExpirationYear: string;
}> => {
  const client = getApiClient();

  try {
    const response = await client.get(`/cards/secrets/${cardID}`, {
      headers: {
        sessionID,
      },
    });

    // If successful
    if (response.status === 200 && response.data?.data) {
      return response.data.data;
    }

    // Fallback if 200 but no data
    throw new Error("Unexpected response format while fetching card secrets.");
  } catch (error) {
    console.error("[getCardSecrets]", error);
    throw new Error(extractErrorMessage(error, "Failed to fetch card secrets"));
  }
};

/**
 * Fetches the spending limit of a specific card.
 *
 * @param cardID - The UUID of the card.
 * @returns An object containing the amount (in cents) and frequency of the limit.
 * @throws If the request fails or returns invalid data.
 */
export const getCardLimit = async (
  cardID: string
): Promise<{
  amount: number; // in cents
  frequency: CardLimitFrequency;
}> => {
  const client = getApiClient();

  try {
    const response = await client.get(`/cards/limit/${cardID}`);

    // If successful
    if (response.status === 200 && response.data?.data) {
      return response.data.data;
    }

    // Fallback if 200 but no data
    throw new Error("Unexpected response format while fetching card limits.");
  } catch (error) {
    console.error("[getCardLimit]", error);
    throw new Error(extractErrorMessage(error, "Failed to fetch card limits"));
  }
};

/**
 * Updates the spending limit of a card.
 *
 * @param limit - The new spending limit in cents.
 * @returns An object indicating success or failure with an optional message.
 */
export const updateLimit = async (
  limit: number,
  cardID: string
): Promise<{ success: boolean; message?: string }> => {
  const client = getApiClient();

  try {
    const response = await client.patch<{ success: boolean; message?: string }>(
      `/cards/limits`,
      { limit, frequency: "per24HourPeriod", cardID }
    );

    if (response.status === 200) {
      return { success: true };
    }

    return {
      success: false,
      message: response.data?.message || "Failed to update limit",
    };
  } catch (error) {
    console.error("[updateLimit]", error);
    return {
      success: false,
      message: extractErrorMessage(error, "Failed to update limit"),
    };
  }
};

/**
 * Fetches the public encryption key for card-related encryption operations.
 *
 * @returns An object containing the PEM-formatted public key.
 */
export const getCardPublicKey = async (): Promise<{ data: string }> => {
  const client = getApiClient();
  const response = await client.get<{ data: string }>("/cards/pubkey");
  return response.data;
};

/**
 * Retrieves transactions for a user's card.
 *
 * @param cardID - The UUID of the card.
 * @param limit - Optional maximum number of transactions to fetch.
 * @param cursor - Optional pagination cursor.
 * @returns A `TransactionsResponse` containing the transaction list and pagination data.
 */
export const getTransactions = async (
  cardID: string,
  limit?: number,
  cursor?: string
): Promise<TransactionsResponse> => {
  // Return mock data

  // await waitFor(2000);
  // return transactionsMockData;
  // Real API call
  try {
    const client = getApiClient();
    const response = await client.get<import("./types").TransactionsResponse>(
      `cards/transactions/${cardID}`,
      {
        params: {
          limit,
          cursor,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("[getTransactions]", error);
    // Return a default structure on error to prevent crashes
    return {
      data: {
        transactions: [],
        pagination: {
          limit: limit || 50,
          has_next: false,
        },
      },
    };
  }
};

/**
 * Fetches on-chain transaction details for a given card transaction.
 *
 * @param transactionID - The transaction identifier.
 * @returns An `OnchainTransactionDetailsResponse` object.
 */
export const getOnchainTransactionDetails = async (
  transactionID: string
): Promise<OnchainTransactionDetailsResponse> => {
  // Return mock data
  // return getMockOnchainTransactionDetails();

  // Real API call (commented out - uncomment to use real API)
  try {
    const client = getApiClient();
    const response = await client.get<OnchainTransactionDetailsResponse>(
      `cards/transactions/${transactionID}/onchain`
    );
    return response.data;
  } catch (error) {
    console.error("[getOnchainTransactionDetails]", error);
    return { data: [] };
  }
};

/**
 * Checks whether the current user is located in the United States.
 *
 * @returns An object containing a boolean `data` field.
 */
export const getUserIsUs = async (): Promise<{ data: boolean }> => {
  try {
    const client = getApiClient();
    const response = await client.get<{ data: boolean }>(`cards/user/is_us`);
    return response.data;
  } catch (error) {
    console.error("[getUserIsUs]", error);
    return { data: false };
  }
};

/**
 * Retrieves a preview of Euler credit and collateral amounts for specific vaults.
 *
 * @param vaults - List of vault addresses to preview.
 * @param creditType - The type of credit (defaults to `"euler"`).
 * @returns An object containing credit and collateral amounts as strings.
 */
export const getEulerCardPreview = async (
  vaults: string[]
): Promise<{ data: { credit: string; collateral: string } }> => {
  try {
    const client = getApiClient();
    const creditType: CreditProviderId = "euler";
    const eulerConfig = getProviderConfig(creditType);
    const response = await client.post<{
      data: { credit: string; collateral: string };
    }>(`/cards/users/credit/${creditType}/preview`, {
      vaults,
      ...eulerConfig,
      chainID: BASE_CHAIN_ID, // Euler is only on Base
    });
    return response.data;
  } catch (error) {
    console.error("[getEulerCardPreview]", error);
    return { data: { credit: "0", collateral: "0" } };
  }
};

/**
 * Fetches the Euler health factor for the user.
 *
 * @returns An object containing the health factor as a string.
 */
export const getEulerHealthFactor = async (): Promise<{
  data: { healthFactor: string };
}> => {
  try {
    const client = getApiClient();
    const creditType: CreditProviderId = "euler";
    const eulerConfig = getProviderConfig(creditType);

    if (!eulerConfig?.borrowVaultAddress) {
      return { data: { healthFactor: "0" } };
    }

    const response = await client.get<{
      data: { healthFactor: string };
    }>(
      `/cards/users/${creditType}/hf/${eulerConfig.borrowVaultAddress}/${BASE_CHAIN_ID}`
    );

    return response.data;
  } catch (error) {
    console.error("[getEulerHealthFactor]", error);
    return { data: { healthFactor: "0" } };
  }
};

/**
 * Fetches the pending transaction balance for a specific card.
 *
 * @param cardID - The UUID of the card.
 * @returns An object containing the status and USD balance of pending transactions.
 */
export const getPendingTransactionBalance = async (
  cardID: string
): Promise<{
  data: {
    status: string;
    balances: { USD: number };
  };
}> => {
  try {
    const client = getApiClient();
    const response = await client.get<{
      data: {
        status: string;
        balances: { USD: number };
      };
    }>(`/cards/transactions/balances/pending/${cardID}`);
    return response.data;
  } catch (error) {
    console.error("[getPendingTransactionBalances]", error);
    return { data: { status: "error", balances: { USD: 0 } } };
  }
};

/**
 * Retrieves the total Euler assets for a specific blockchain network.
 *
 * @returns An `EulerTotalAssetResponse` containing total assets in USD.
 */
export const getEulerTotalAsset =
  async (): Promise<EulerTotalAssetResponse> => {
    try {
      const client = getApiClient();
      const response = await client.get<EulerTotalAssetResponse>(
        `/cards/users/euler/assets/${BASE_CHAIN_ID}`
      );
      return response.data;
    } catch (error) {
      console.error("[getEulerTotalAsset]", error);
      return { data: { totalAssetsUSD: 0 } };
    }
  };

/**
 * Retrieves the user's hook transaction history.
 *
 * @param limit - The maximum number of transactions to retrieve.
 * @param offset - The number of transactions to skip.
 * @returns A promise that resolves to an array of hook transactions.
 */
export const getHookTransactions = async (
  limit?: number,
  offset?: number
): Promise<HookTransaction[]> => {
  try {
    const client = getApiClient();
    const response = await client.get<{ data: HookTransaction[] }>(
      "/cards/transactions/hook",
      {
        params: { limit, offset },
      }
    );
    return response.data.data;
  } catch (error) {
    console.error("[getHookTransactions]", error);
    // Safe fallback
    return [];
  }
};

/**
 * Updates a card hook with the provided configuration.
 *
 * @param cardID - The UUID of the card.
 * @param chainID - The chain ID associated with the hook.
 * @param hookType - The type of hook to update (e.g., "yield_buy").
 * @param config - The configuration object for the hook.
 * @returns A promise that resolves to an object indicating success or failure,
 *          along with an optional message.
 */
export const updateCardHook = async (
  cardID: string,
  chainID: number,
  hookType: HookType,
  config: HookConfig<HookType>
): Promise<{ success: boolean; message?: string }> => {
  const client = getApiClient();

  try {
    const response = await client.patch<{ success: boolean; message?: string }>(
      `/cards/hook`,
      {
        cardID,
        config,
        hookType,
        chainID,
      }
    );

    if (response.status === 200) {
      return { success: true };
    }

    return {
      success: false,
      message: response.data?.message || "Failed to update card hook",
    };
  } catch (error: any) {
    console.error("[updateCardHook] Error:", error);
    return {
      success: false,
      message:
        error?.response?.data?.data?.error ||
        error?.message ||
        "Failed to update card hook",
    };
  }
};

/**
 * Deletes a card hook with the provided configuration.
 *
 * @param cardID - The UUID of the card.
 * @returns A promise that resolves to an object indicating success or failure,
 *          along with an optional message.
 */
export const deleteCardHook = async (
  cardID: string
): Promise<{
  success: boolean;
  message?: string;
}> => {
  const client = getApiClient();

  try {
    // pass params to delete
    const response = await client.delete(`/cards/hook/${cardID}`);

    if (response.status === 200) {
      return { success: true };
    }

    return {
      success: false,
      message: response.data?.message || "Failed to delete card hook",
    };
  } catch (error: any) {
    console.error("[deleteCardHook] Error:", error);
    return {
      success: false,
      message:
        error?.response?.data?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete card hook",
    };
  }
};
